/**
 * Subscription quantity discounts by delivery frequency (not per product line).
 * Config: Theme settings → Active Discounts → Subscription quantity discounts
 * Exposed on window.store.activeDiscounts (subscriptionQuantityDiscounts entry).
 */
(function () {
	function getSubscriptionConfig() {
		const discounts = window.store?.activeDiscounts || [];
		return discounts.find((entry) => entry.subscriptionQuantityDiscounts === true) || null;
	}

	function isEnabled() {
		if (!getSubscriptionConfig()) return false;
		if (!getEligibleHandles().length) return false;
		if (!getDiscountTiers().length) return false;

		return true;
	}

	function normalizeDiscountPercent(value) {
		const n = Number(value);
		if (Number.isNaN(n)) return 0;
		if (n > 0 && n < 1) return Math.round(n * 100);
		return n;
	}

	function getDiscountTiers() {
		const config = getSubscriptionConfig();
		const rules = Array.isArray(config?.rules) ? config.rules : [];
		if (!rules.length) return [];

		return rules
			.map((row) => ({
				quantity: Number(row.quantity),
				discount: normalizeDiscountPercent(row.discount),
			}))
			.filter((row) => row.quantity > 0)
			.sort((a, b) => a.quantity - b.quantity);
	}

	function getEligibleHandles() {
		const config = getSubscriptionConfig();
		if (!config) return [];
		return Array.isArray(config.eligibleProducts) ? config.eligibleProducts.filter(Boolean) : [];
	}

	function isProductEligible(handle) {
		if (!handle || !isEnabled()) return false;
		return getEligibleHandles().includes(handle);
	}

	function getDiscountForQuantity(quantity) {
		const qty = Math.max(0, Number(quantity) || 0);
		if (qty <= 0) return 0;

		const tiers = getDiscountTiers();
		if (!tiers.length) return 0;

		const maxTierQty = tiers[tiers.length - 1].quantity;
		const tierQty = Math.min(qty, maxTierQty);
		const tier = tiers.find((row) => row.quantity === tierQty) || tiers[tiers.length - 1];

		return tier?.discount ?? normalizeDiscountPercent(tiers[tiers.length - 1].discount);
	}

	function stripHtml(html) {
		if (!html) return '';
		const tmp = document.createElement('div');
		tmp.innerHTML = html;
		return (tmp.textContent || tmp.innerText || '').trim();
	}

	function parseFrequencyDays(text) {
		const normalized = stripHtml(text).toLowerCase();
		const match = normalized.match(/(\d+)\s*days?/);
		return match ? parseInt(match[1], 10) : null;
	}

	function getLineFrequencyDays(line) {
		const plan = line?.selling_plan_allocation?.selling_plan;
		if (!plan) return null;
		return parseFrequencyDays(plan.description || plan.name || '');
	}

	function getLineDiscountPercent(line) {
		const plan = line?.selling_plan_allocation?.selling_plan;
		if (!plan) return null;
		const adjustment = plan.price_adjustments?.[0]?.value;
		if (typeof adjustment === 'number') return adjustment;
		const fromName = stripHtml(plan.name || plan.description || '').match(/\(-?\s*(\d+)\s*%\)/);
		return fromName ? parseInt(fromName[1], 10) : null;
	}

	function productJsonUrl(handle) {
		const base = (window.theme?.routes?.root_url || '/').replace(/\/$/, '');
		return `${base}/products/${handle}.js`;
	}

	async function fetchProduct(handle) {
		window.store = window.store || {};
		window.store.subscriptionProductPlans = window.store.subscriptionProductPlans || {};

		if (window.store.subscriptionProductPlans[handle]) {
			return window.store.subscriptionProductPlans[handle];
		}

		const response = await fetch(productJsonUrl(handle));
		const data = await response.json();
		window.store.subscriptionProductPlans[handle] = data;
		return data;
	}

	function findSellingPlanId(product, frequencyDays, discountPercent) {
		const groups = product?.selling_plan_groups || [];

		for (const group of groups) {
			for (const plan of group.selling_plans || []) {
				const planDays = parseFrequencyDays(plan.description || plan.name || '');
				const planDiscount = plan.price_adjustments?.[0]?.value;

				if (planDays === frequencyDays && planDiscount === discountPercent) {
					return plan.id;
				}
			}
		}

		return null;
	}

	function getSubscriptionLines(cart) {
		const eligible = new Set(getEligibleHandles());
		return (cart?.items || []).filter(
			(item) => item.selling_plan_allocation && eligible.has(item.handle)
		);
	}

	function buildReconciliationPlan(cart) {
		const subscriptionLines = getSubscriptionLines(cart);
		const removals = [];
		const additions = [];
		const frequencyGroups = new Map();

		subscriptionLines.forEach((line) => {
			const frequencyDays = getLineFrequencyDays(line);
			if (frequencyDays == null) return;

			const key = String(frequencyDays);
			if (!frequencyGroups.has(key)) frequencyGroups.set(key, []);
			frequencyGroups.get(key).push(line);
		});

		frequencyGroups.forEach((groupLines, frequencyKey) => {
			const frequencyDays = parseInt(frequencyKey, 10);
			const totalQty = groupLines.reduce((sum, line) => sum + line.quantity, 0);
			const targetDiscount = getDiscountForQuantity(totalQty);
			const byHandle = new Map();

			groupLines.forEach((line) => {
				if (!byHandle.has(line.handle)) byHandle.set(line.handle, []);
				byHandle.get(line.handle).push(line);
			});

			byHandle.forEach((handleLines, handle) => {
				const handleQty = handleLines.reduce((sum, line) => sum + line.quantity, 0);
				const needsFix =
					handleLines.length > 1 ||
					handleLines.some((line) => getLineDiscountPercent(line) !== targetDiscount);

				if (!needsFix) return;

				handleLines.forEach((line) => {
					if (!removals.includes(line.key)) removals.push(line.key);
				});

				additions.push({
					handle,
					variantId: handleLines[0].variant_id,
					quantity: handleQty,
					frequencyDays,
					targetDiscount,
					properties: { ...(handleLines[0].properties || {}) },
				});
			});
		});

		return { removals, additions };
	}

	function getSectionsParam() {
		const routes = window.theme?.routes || {};
		const cartUrl = routes.cart_url || '/cart';
		const isCartPage = window.location.pathname === new URL(cartUrl, window.location.origin).pathname;
		return isCartPage ? 'cart-drawer,cart-bubble,main-cart' : 'cart-drawer,cart-bubble';
	}

	async function fetchCart() {
		const root = (window.theme?.routes?.root_url || '/').replace(/\/$/, '');
		const response = await fetch(`${root}/cart.js`);
		return response.json();
	}

	async function reconcile(cart, options = {}) {
		if (!isEnabled()) {
			return { changed: false, cart, responseText: null };
		}

		const currentCart = cart || (await fetchCart());
		const plan = buildReconciliationPlan(currentCart);

		if (!plan.removals.length && !plan.additions.length) {
			return { changed: false, cart: currentCart, responseText: null };
		}

		const sections = options.sections || getSectionsParam();
		const sectionsUrl = options.sectionsUrl || window.location.pathname;
		let responseText = null;

		if (plan.removals.length) {
			const updates = {};
			plan.removals.forEach((key) => {
				updates[key] = 0;
			});

			const root = (window.theme?.routes?.root_url || '/').replace(/\/$/, '');
			const updateResponse = await fetch(`${root}/cart/update.js`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({
					updates,
					sections,
					sections_url: sectionsUrl,
				}),
			});
			responseText = await updateResponse.text();
		}

		const itemsToAdd = [];

		for (const addition of plan.additions) {
			const product = await fetchProduct(addition.handle);
			const sellingPlanId = findSellingPlanId(product, addition.frequencyDays, addition.targetDiscount);

			if (!sellingPlanId) {
				console.warn('[SubscriptionQuantityDiscount] Missing selling plan', addition);
				continue;
			}

			itemsToAdd.push({
				id: addition.variantId,
				quantity: addition.quantity,
				selling_plan: sellingPlanId,
				properties: {
					...(addition.properties || {}),
					_selected_selling_plan_ids: JSON.stringify([String(sellingPlanId)]),
				},
			});
		}

		if (itemsToAdd.length) {
			const root = (window.theme?.routes?.root_url || '/').replace(/\/$/, '');
			const addResponse = await fetch(`${root}/cart/add.js`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({
					items: itemsToAdd,
					sections,
					sections_url: sectionsUrl,
				}),
			});
			responseText = await addResponse.text();
		}

		const updatedCart = await fetchCart();

		return { changed: true, cart: updatedCart, responseText };
	}

	function applyCartSections(responseText) {
		if (!responseText) return;

		try {
			const parsed = JSON.parse(responseText);
			const cartUrl = window.theme?.routes?.cart_url || '/cart';
			const isCartPage = window.location.pathname === new URL(cartUrl, window.location.origin).pathname;

			if (isCartPage && typeof Cart !== 'undefined' && Cart.applySectionsFromResponse) {
				Cart.applySectionsFromResponse(parsed);
			} else if (window.CartDrawer?.updateCartUI) {
				window.CartDrawer.updateCartUI(responseText);
			}
		} catch (error) {
			console.error('[SubscriptionQuantityDiscount] Failed to apply cart sections', error);
		}

		if (typeof window.getCartData === 'function') {
			window.getCartData();
		}
	}

	window.SubscriptionQuantityDiscount = {
		isEnabled,
		isProductEligible,
		getSubscriptionConfig,
		getDiscountTiers,
		getEligibleHandles,
		getDiscountForQuantity,
		parseFrequencyDays,
		fetchProduct,
		findSellingPlanId,
		buildReconciliationPlan,
		reconcile,
		applyCartSections,
	};
})();
